/**
 * Same-road / same-turn agreement score between Valhalla geometry and Mapbox match.
 * Pure JS (no Turf) for portability and easy Jest runs.
 */

import { haversineMeters } from './densifyRoutePoints'

const CORRIDOR_WEIGHT = 0.6
const LENGTH_WEIGHT = 0.2
const ENDPOINT_WEIGHT = 0.1
const CONFIDENCE_WEIGHT = 0.1

const CORRIDOR_THRESHOLD_M = 25
const ENDPOINT_GOOD_M = 30
const ENDPOINT_OK_M = 75
const PATH_DIVERGED_CORRIDOR = 70
const PATH_DIVERGED_LENGTH_PCT = 8

/**
 * Approximate distance from point to polyline (meters).
 */
export function pointToPolylineDistanceM(point, lineCoordsLonLat) {
  if (!lineCoordsLonLat || lineCoordsLonLat.length < 2) {
    return Infinity
  }
  let min = Infinity
  for (let i = 0; i < lineCoordsLonLat.length - 1; i++) {
    const start = {
      lon: lineCoordsLonLat[i][0],
      lat: lineCoordsLonLat[i][1],
    }
    const end = {
      lon: lineCoordsLonLat[i + 1][0],
      lat: lineCoordsLonLat[i + 1][1],
    }
    min = Math.min(min, pointToSegmentDistanceM(point, start, end))
  }
  return min
}

function pointToSegmentDistanceM(point, start, end) {
  // Equirectangular local projection for short segments
  const lat0 = ((start.lat + end.lat + point.lat) / 3) * (Math.PI / 180)
  const cos = Math.cos(lat0)
  const ax = start.lon * cos
  const ay = start.lat
  const bx = end.lon * cos
  const by = end.lat
  const px = point.lon * cos
  const py = point.lat
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  let ratio = 0
  if (len2 > 0) {
    ratio = ((px - ax) * dx + (py - ay) * dy) / len2
    ratio = Math.max(0, Math.min(1, ratio))
  }
  const cx = ax + ratio * dx
  const cy = ay + ratio * dy
  // convert back approx
  const closest = { lon: cos !== 0 ? cx / cos : start.lon, lat: cy }
  return haversineMeters(point, closest)
}

function polylineLengthM(lineCoordsLonLat) {
  let sum = 0
  for (let i = 0; i < lineCoordsLonLat.length - 1; i++) {
    sum += haversineMeters(
      { lon: lineCoordsLonLat[i][0], lat: lineCoordsLonLat[i][1] },
      { lon: lineCoordsLonLat[i + 1][0], lat: lineCoordsLonLat[i + 1][1] }
    )
  }
  return sum
}

/**
 * @param {Array<{ lon: number, lat: number }>} valhallaGeometry
 * @param {GeoJSON.LineString|null} mapboxLine
 * @param {{ confidence?: number, mapboxDistanceM?: number, valhallaLengthMiles?: number }} [meta]
 */
export function computeMatchingFactor(valhallaGeometry, mapboxLine, meta = {}) {
  if (!Array.isArray(valhallaGeometry) || valhallaGeometry.length < 2) {
    return {
      matchingFactor: 0,
      pathDiverged: true,
      details: { reason: 'missing_valhalla_geometry' },
    }
  }
  if (
    !mapboxLine ||
    mapboxLine.type !== 'LineString' ||
    !Array.isArray(mapboxLine.coordinates) ||
    mapboxLine.coordinates.length < 2
  ) {
    return {
      matchingFactor: 0,
      pathDiverged: true,
      details: { reason: 'missing_mapbox_geometry' },
    }
  }

  const lineCoords = mapboxLine.coordinates

  const step = Math.max(1, Math.floor(valhallaGeometry.length / 80))
  let within = 0
  let samples = 0
  for (let i = 0; i < valhallaGeometry.length; i += step) {
    const distM = pointToPolylineDistanceM(valhallaGeometry[i], lineCoords)
    samples += 1
    if (distM <= CORRIDOR_THRESHOLD_M) {
      within += 1
    }
  }
  const corridorPct = samples ? (within / samples) * 100 : 0

  let valhallaLenM = 0
  for (let i = 0; i < valhallaGeometry.length - 1; i++) {
    valhallaLenM += haversineMeters(
      valhallaGeometry[i],
      valhallaGeometry[i + 1]
    )
  }
  if (meta.valhallaLengthMiles != null) {
    valhallaLenM = meta.valhallaLengthMiles * 1609.344
  }
  const mapboxLenM =
    meta.mapboxDistanceM != null
      ? meta.mapboxDistanceM
      : polylineLengthM(lineCoords)
  const lengthRatio =
    valhallaLenM > 0
      ? 1 - Math.min(1, Math.abs(valhallaLenM - mapboxLenM) / valhallaLenM)
      : 0
  const lengthPctScore = lengthRatio * 100
  const lengthDriftPct =
    valhallaLenM > 0
      ? (Math.abs(valhallaLenM - mapboxLenM) / valhallaLenM) * 100
      : 100

  const vStart = valhallaGeometry[0]
  const vEnd = valhallaGeometry[valhallaGeometry.length - 1]
  const mStart = {
    lon: lineCoords[0][0],
    lat: lineCoords[0][1],
  }
  const mEnd = {
    lon: lineCoords[lineCoords.length - 1][0],
    lat: lineCoords[lineCoords.length - 1][1],
  }
  const startDist = haversineMeters(vStart, mStart)
  const endDist = haversineMeters(vEnd, mEnd)
  const endpointScore = (scoreEndpoint(startDist) + scoreEndpoint(endDist)) / 2

  const confidence =
    typeof meta.confidence === 'number'
      ? Math.max(0, Math.min(1, meta.confidence)) * 100
      : 70

  const matchingFactor = Math.round(
    corridorPct * CORRIDOR_WEIGHT +
      lengthPctScore * LENGTH_WEIGHT +
      endpointScore * ENDPOINT_WEIGHT +
      confidence * CONFIDENCE_WEIGHT
  )

  const pathDiverged =
    corridorPct < PATH_DIVERGED_CORRIDOR ||
    lengthDriftPct > PATH_DIVERGED_LENGTH_PCT

  return {
    matchingFactor: Math.max(0, Math.min(100, matchingFactor)),
    pathDiverged,
    details: {
      corridorPct,
      lengthDriftPct,
      startDistM: startDist,
      endDistM: endDist,
      confidence,
      valhallaLenM,
      mapboxLenM,
    },
  }
}

function scoreEndpoint(distM) {
  if (distM <= ENDPOINT_GOOD_M) {
    return 100
  }
  if (distM >= ENDPOINT_OK_M) {
    return 0
  }
  return (
    100 * (1 - (distM - ENDPOINT_GOOD_M) / (ENDPOINT_OK_M - ENDPOINT_GOOD_M))
  )
}
